import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";

import { AlertTriangle, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";

import type {
  ContactId,
  OpportunityId,
  OrganizationId,
} from "@goose/schema/Salesforce";

import { filterMap, None, Some } from "@goose/utils/option";
import { UUID } from "@goose/utils/uuid";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";
import { EvaluationIcon } from "~/components/admin/Icons";
import { PageHeader } from "~/components/admin/PageHeader";
import { Surface } from "~/components/admin/Surface";
import { ErrorDisplay } from "~/components/ErrorDisplay";
import { EvaluationDetails } from "~/components/EvaluationDetails";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { AlertDialog } from "~/components/ui/AlertDialog";
import { Badge } from "~/components/ui/Badge";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations/$evaluationId",
)({
  component: RouteComponent,
  params: {
    parse: ({ evaluationId }) => {
      return {
        evaluationId: UUID.from(evaluationId),
      };
    },
    stringify: ({ evaluationId }) => {
      return {
        evaluationId: evaluationId.toShort(),
      };
    },
  },
  beforeLoad: ({ params, location }) => {
    // Canonicalize long-form UUID URLs to the Base58 short form.
    if (!location.pathname.endsWith(params.evaluationId.toShort())) {
      throw redirect({
        to: "/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations/$evaluationId",
        params,
        replace: true,
      });
    }
  },
});

function RouteComponent() {
  const trpc = useTRPC();
  const { organizationId, opportunityId, contactId, evaluationId } =
    Route.useParams();

  const evaluation = useQuery(
    trpc.contacts.evaluations.get.queryOptions({
      id: evaluationId.toString(),
    }),
  );

  return (
    <QueryState
      result={evaluation}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => {
        if (data.contactId !== contactId) {
          return (
            <ErrorDisplay message="Evaluation not found" level="warning" />
          );
        }

        return (
          <div className="mt-6 space-y-6">
            <EvaluationSummary
              organizationId={organizationId}
              opportunityId={opportunityId}
              contactId={contactId}
              evaluation={data}
            />
            <EvaluationResults evaluation={data} />
          </div>
        );
      }}
    </QueryState>
  );
}

type Evaluation = InferTransformedProcedureOutput<"contacts.evaluations.get">;

interface EvaluationSummaryProps {
  organizationId: OrganizationId;
  opportunityId: OpportunityId;
  contactId: ContactId;
  evaluation: Evaluation;
}

function EvaluationSummary({
  organizationId,
  opportunityId,
  contactId,
  evaluation,
}: EvaluationSummaryProps) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const onDelete = async () => {
    await queryClient.invalidateQueries({
      queryKey: trpc.contacts.evaluations.list.queryKey({
        organizationId,
        contactId,
      }),
    });

    await navigate({
      to: "/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations",
      params: { organizationId, opportunityId, contactId },
    });
  };

  return (
    <>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Link
              to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations"
              params={{ organizationId, opportunityId, contactId }}
            >
              Evaluations
            </Link>
            <span>{UUID.fromString(evaluation.id).toShort()}</span>
          </Breadcrumbs>
        }
        icon={EvaluationIcon}
        title="Evaluation"
        actions={
          <EvaluationDeleteButton
            evaluationId={evaluation.id}
            onDelete={() => {
              void onDelete();
            }}
          />
        }
      />

      <DetailsTable
        details={[
          {
            label: "ID",
            value: UUID.fromString(evaluation.id).toShort(),
          },
          {
            label: "Created by",
            value: (
              <Link
                to="/admin/organizations/$organizationId/users/$userId"
                className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                params={{
                  organizationId: evaluation.createdBy.organizationId,
                  userId: evaluation.createdBy.id,
                }}
              >
                {evaluation.createdBy.name}
              </Link>
            ),
          },
          {
            label: "Score",
            value: `${Math.round(evaluation.score * 100)}%`,
          },
          {
            label: "Needs clarification",
            value: evaluation.needsClarification ? (
              <Badge variant="destructive" size="sm">
                Yes
              </Badge>
            ) : (
              <Badge variant="success" size="sm">
                No
              </Badge>
            ),
          },
          {
            label: "Requirement set",
            value: (
              <Link
                to="/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId"
                className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                params={{
                  organizationId,
                  opportunityId,
                  requirementsId: UUID.fromString(evaluation.requirementSetId),
                }}
              >
                {UUID.fromString(evaluation.requirementSetId).toShort()}
              </Link>
            ),
          },
          evaluation.parentId && {
            label: "Parent",
            value: (
              <Link
                to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations/$evaluationId"
                className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                params={{
                  organizationId,
                  opportunityId,
                  contactId,
                  evaluationId: UUID.fromString(evaluation.parentId),
                }}
              >
                {UUID.fromString(evaluation.parentId).toShort()}
              </Link>
            ),
          },
          evaluation.clarification && {
            label: "Clarification",
            value: evaluation.clarification,
          },
          {
            label: "Model usage",
            value: `${evaluation.modelUsage.model}  (${evaluation.modelUsage.inputTokenCount} in / ${evaluation.modelUsage.outputTokenCount} out)`,
          },
          evaluation.sourceHash && {
            label: "Source hash",
            value: evaluation.sourceHash,
          },
          {
            label: "Created at",
            value: formatDate(evaluation.createdAt),
          },
        ]}
      />

      <Surface as="section" className="overflow-hidden">
        <Surface.Header>
          <Surface.HeaderContent>
            <Surface.HeaderTitle>Summary</Surface.HeaderTitle>
          </Surface.HeaderContent>
        </Surface.Header>
        <Surface.Content className="px-6 py-5">
          {evaluation.summary}
        </Surface.Content>
      </Surface>
    </>
  );
}

interface EvaluationDeleteButtonProps {
  evaluationId: string;
  onDelete: () => void;
}

function EvaluationDeleteButton({
  evaluationId,
  onDelete,
}: EvaluationDeleteButtonProps) {
  const [open, setOpen] = useState(false);
  const trpc = useTRPC();

  const deleteEvaluation = useMutation(
    trpc.contacts.evaluations.remove.mutationOptions(),
  );

  const handleDelete = async () => {
    await deleteEvaluation.mutateAsync({
      id: evaluationId,
    });
    onDelete();
    setOpen(false);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !deleteEvaluation.isPending) {
          setOpen(false);
        }
      }}
    >
      <Button asChild variant="destructive" size="sm">
        <AlertDialog.Trigger
          onClick={() => {
            setOpen(true);
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </AlertDialog.Trigger>
      </Button>
      <AlertDialog.Content className="max-w-md">
        <AlertDialog.Title>Delete evaluation</AlertDialog.Title>
        <AlertDialog.Description>
          This permanently removes the evaluation record from the candidate
          history for this opportunity. Are you sure you want to delete this
          evaluation?
        </AlertDialog.Description>

        {deleteEvaluation.isError ? (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertTriangle className="mr-2 h-4 w-4" />
            {deleteEvaluation.error.message}
          </div>
        ) : null}

        <AlertDialog.Footer>
          <AlertDialog.Close disabled={deleteEvaluation.isPending}>
            Cancel
          </AlertDialog.Close>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void handleDelete();
            }}
            disabled={deleteEvaluation.isPending}
          >
            {deleteEvaluation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
}

interface EvaluationResultsProps {
  evaluation: Evaluation;
}

function EvaluationResults({ evaluation }: EvaluationResultsProps) {
  const trpc = useTRPC();
  const requirementSet = useQuery(
    trpc.opportunities.requirements.get.queryOptions({
      id: evaluation.requirementSetId,
    }),
  );

  const results = useMemo(() => {
    if (!requirementSet.data) {
      return [];
    }

    const requirementsById = new Map(
      requirementSet.data.requirements.map((requirement) => [
        requirement.id,
        requirement,
      ]),
    );

    return filterMap(evaluation.results, (fit) => {
      const requirement = requirementsById.get(fit.requirementId);
      if (!requirement) {
        return None;
      }
      return Some({ fit, requirement });
    });
  }, [evaluation.results, requirementSet.data]);

  return (
    <QueryState
      result={requirementSet}
      loading={<Spinner size="sm" />}
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      <EvaluationDetails results={results} showDebug />
    </QueryState>
  );
}
